import React from 'react';
import { Global, css } from '@emotion/react';
import tw, { GlobalStyles as BaseStyles } from 'twin.macro';
import '@fontsource/geologica/700.css';
import '@fontsource/geologica/900.css';

const customStyles = css`
  html,
  body {
    ${tw`text-neutral-intense`}
    width: 100%;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system,
      BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans,
      sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol,
      Noto Color Emoji;
    line-height: 1.2;
  }

  h1 {
    ${tw`text-3xl font-bold font-heading leading-9`}
  }

  h2 {
    ${tw`text-2xl font-bold`}
  }

  h3 {
    ${tw`text-xl font-bold leading-6`}
  }

  h4 {
    ${tw`text-lg font-bold`}
  }

  h5 {
    ${tw`text-base font-bold`}
  }

  body {
    ${tw`antialiased`}
    min-height: 100vh;
    min-height: -webkit-fill-available;
  }

  @media screen {
    html,
    body {
      ${tw`bg-brand-quiet`}
    }
  }

  html {
    height: -webkit-fill-available;
  }

  .fr-active {
    opacity: 1 !important;
  }

  /* TSH-415 - Fix table cell colorpicker : keep the specific selector to avoid breaking froala somewhere else */

  body > .fr-popup > .fr-table-colors {
    max-height: 200px !important;
    overflow: auto !important;
  }

  a[disabled] {
    pointer-events: none;
  }

  .ql-size-large {
    font-size: 16px;
  }

  .ql-size-small {
    font-size: 12px;
  }

  a {
    ${tw`text-brand-intense`}
  }

  /* TSH-437 - style user links (+ add icons outside of editor and print modes) */

  @font-face {
    font-family: 'FontAwesome';
    font-style: normal;
    font-weight: 900;
    font-display: block;
    src: url('/webfonts/fa-solid-900-subset.woff2') format('woff2'),
      url('/webfonts/fa-solid-900-subset.ttf') format('truetype');
  }

  .fr-view a {
    ${tw`text-brand-moderate! hover:text-brand-intense! underline! underline-offset-2!`};
  }

  .html-view:not(.print) .fr-view {
    /* render default text color as a dark blue for UI consistency */
    /* render default text color as black in print */
    ${tw`text-neutral-moderate`};
  }

  .fr-view hr {
    // override froala's break-after style to avoid forcing a page break after hr
    ${tw`break-after-auto`};
  }

  .html-view .fr-view a {
    &::after {
      content: '\\F054';
      font-family: 'FontAwesome';
      margin-left: 0.25rem;
      display: inline-block;
      font-size: 0.5em;
      line-height: normal;
      ${tw`print:hidden`};
    }

    &:focus {
      ${tw`outline-2 outline-blue-800 px-0.5 -ml-0.5`};
    }

    &[target='_blank']::after {
      content: '\\f08E';
    }
  }

  .html-view.print .fr-view a {
    ${tw`mr-0 min-w-0`};

    &::after {
      ${tw`hidden`};
    }
  }

  .overlayEnter {
    ${tw`transform transition ease-out duration-200`}
  }

  .overlayEnterFrom {
    ${tw`opacity-0 translate-y-1`}
  }

  .overlayEnterTo {
    ${tw`opacity-100 translate-y-0`}
  }

  .overlayLeave {
    ${tw`transform transition ease-in duration-150`}
  }

  .overlayLeaveFrom {
    ${tw`opacity-100 translate-y-0`}
  }

  .overlayLeaveTo {
    ${tw`opacity-0 translate-y-1`}
  }

  .dialogEnter {
    ${tw`ease-out duration-300`}
  }

  .dialogEnterFrom {
    ${tw`opacity-0 scale-95`}
  }

  .dialogEnterTo {
    ${tw`opacity-100 scale-100`}
  }

  .dialogLeave {
    ${tw`ease-in duration-200`}
  }

  .dialogLeaveFrom {
    ${tw`opacity-100 scale-100`}
  }

  .dialogLeaveTo {
    ${tw`opacity-0 scale-95`}
  }

  .drawerEnter {
    ${tw`transition ease-in-out duration-300 transform`}
  }

  .drawerEnterFrom {
    ${tw`translate-x-full`}
  }

  .drawerEnterTo {
    ${tw`translate-x-0`}
  }

  .drawerLeave {
    ${tw`transition ease-in-out duration-300 transform`}
  }

  .drawerLeaveFrom {
    ${tw`translate-x-0`}
  }

  .drawerLeaveTo {
    ${tw`translate-x-full`}
  }

  .menuEnter {
    ${tw`transition ease-out duration-100`}
  }

  .menuEnterFrom {
    ${tw`transform opacity-0 scale-95`}
  }

  .menuEnterTo {
    ${tw`transform opacity-100 scale-100`}
  }

  .menuLeave {
    ${tw`transition ease-in duration-75`}
  }

  .menuLeaveFrom {
    ${tw`transform opacity-100 scale-100`}
  }

  .menuLeaveTo {
    ${tw`transform opacity-0 scale-95`}
  }

  .notifEnter {
    ${tw`transform ease-out duration-300 transition`}
  }

  .notifEnterFrom {
    ${tw`translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2`}
  }

  .notifEnterTo {
    ${tw`translate-y-0 opacity-100 sm:translate-x-0`}
  }

  .notifLeave {
    ${tw`transition ease-in duration-300`}
  }

  .notifLeaveFrom {
    ${tw`opacity-100`}
  }

  .notifLeaveTo {
    ${tw`opacity-0`}
  }

  .disclosureEnter {
    ${tw`transition duration-100 ease-out`}
  }

  .disclosureEnterFrom {
    ${tw`transform scale-95 opacity-0`}
  }

  .disclosureEnterTo {
    ${tw`transform scale-100 opacity-100`}
  }

  .disclosureLeave {
    ${tw`transition duration-75 ease-out`}
  }

  .disclosureLeaveFrom {
    ${tw`transform scale-100 opacity-100`}
  }

  .disclosureLeaveTo {
    ${tw`transform scale-95 opacity-0`}
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    ${tw`bg-neutral-quiet rounded-lg`};
    width: 10px;
  }

  ::-webkit-scrollbar-thumb {
    ${tw`bg-neutral-moderate rounded-lg`}
  }

  .tsh-skeleton {
    ${tw`animate-pulse bg-neutral-moderate cursor-default`}
    &::before,
    &::after,
    * {
      visibility: hidden !important;
    }
  }

  @media print {
    .break-before-avoid {
      page-break-before: avoid;
    }

    .break-after-avoid {
      page-break-after: avoid;
    }

    .break-after-always {
      page-break-after: always;
    }

    .break-before-always {
      page-break-before: always;
    }

    .break-inside-auto {
      page-break-inside: auto;
    }

    .break-inside-avoid {
      page-break-inside: avoid;
    }

    strong {
      font-weight: 700 !important;
    }
  }
`;

export default function AllStyles() {
  return (
    <>
      <BaseStyles />
      <Global styles={customStyles} />
    </>
  );
}
